/* ============================================================================
   GlobalErrorReporter
   Hooks into window.onerror + unhandledrejection and POSTs to /api/errors so
   the admin can see client-side crashes without a third-party SDK. The
   endpoint is anonymous-OK, so this works even before sign-in. Posts are
   throttled to one per 30s per error message to avoid log floods.
   ============================================================================ */
import { useEffect } from "react";
import { Sentry } from "@/lib/sentry";
import { BUILD_ID } from "@/lib/buildId";
import { installStallWatchdog } from "@/lib/stallWatchdog";

interface ErrorPayload {
  message: string;
  stack?: string;
  route?: string;
  level?: "error" | "warn";
}

const recentSends = new Map<string, number>();
const THROTTLE_MS = 30_000;

async function postError(payload: ErrorPayload) {
  const key = `${payload.level ?? "error"}:${payload.message}`;
  const now = Date.now();
  const last = recentSends.get(key) ?? 0;
  if (now - last < THROTTLE_MS) return;
  recentSends.set(key, now);

  try {
    await fetch("/api/errors", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message: String(payload.message ?? "").slice(0, 2000),
        stack: payload.stack ? String(payload.stack).slice(0, 8000) : undefined,
        route: payload.route ?? (typeof window !== "undefined" ? window.location.hash || window.location.pathname : ""),
        level: payload.level ?? "error",
        buildId: BUILD_ID,
      }),
      keepalive: true,
    });
  } catch {
    // Never throw from the reporter itself - swallow.
  }
}

export function GlobalErrorReporter() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const onError = (event: ErrorEvent) => {
      postError({
        message: event.message || "Uncaught error",
        stack: event.error?.stack,
        level: "error",
      });
      try {
        Sentry.captureException(
          event.error instanceof Error
            ? event.error
            : new Error(event.message || "Uncaught error"),
        );
      } catch { /* swallow */ }
    };

    const onRejection = (event: PromiseRejectionEvent) => {
      const reason: any = event.reason;
      const message =
        typeof reason === "string"
          ? reason
          : reason?.message ?? "Unhandled promise rejection";
      postError({
        message,
        stack: reason?.stack,
        level: "error",
      });
      try {
        Sentry.captureException(
          reason instanceof Error ? reason : new Error(message),
        );
      } catch { /* swallow */ }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // Freezes leave no exception to catch — the watchdog turns main-thread
    // stalls (and frozen-then-killed sessions) into /api/errors rows too.
    const uninstallWatchdog = installStallWatchdog(postError);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      uninstallWatchdog();
    };
  }, []);

  return null;
}
